<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="60vh"
      scrollable
    >
      <v-card>
        <v-card-title>
          Project Labels
          <v-spacer></v-spacer>
          <v-btn icon @click="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="py-3">
          <v-data-table
            :headers="tableHeaders"
            :items="labels"
            :items-per-page="10"
          >
            <!-- Name Column -->
            <template v-slot:[`item.Label_Name`]="{ item, index }">
              <v-text-field
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.Label_Name"
                label="Label Name"
                dense
                outlined
              />
              <span v-else>{{ item.Label_Name }}</span>
            </template>

            <!-- Actions Column -->
            <template v-slot:[`item.Actions`]="{ item, index }">
              <v-btn
                :disabled="editMode && (currentRowIndex !== index) || (currentRowIndex === index && canUpdate)"
                :color="editMode && currentRowIndex === index ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item, index)"
              >
                <v-icon>
                  {{ currentRowIndex === index ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>

              <v-btn
                v-if="index !== currentRowIndex"
                small
                icon
                color="red darken-2"
                :disabled="editMode"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>

              <v-btn
                v-if="editMode && currentRowIndex === index"
                color="primary"
                small
                icon
                @click="onCancelClick"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            :disabled="editMode"
            @click="createNewLabelDialog = true"
          >
            Add New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Dialog -->
    <v-dialog
      max-width="500"
      v-model="createNewLabelDialog"
    >
      <v-card>
        <v-card-title>
          New Label
          <v-spacer></v-spacer>
          <v-btn icon @click="createNewLabelDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-text-field
            label="Label Name"
            v-model="newLabelName"
            outlined
            dense
          />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="!newLabelName"
            @click="createNewLabel"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete -->
    <ConfirmOrDeniedDialog
      v-if="deleteLabelDialog"
      v-model="deleteLabelDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteLabel"
      @onCancelClick="cancelDelete"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
  name: 'EmployeeProjectsLabels',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentRowIndex: null,
      currentItem: null,
      newLabelName: '',
      createNewLabelDialog: false,
      labels: [],
      deleteLabelDialog: false,
      editMode: false,
      tableHeaders: [
        {
          text: 'Name',
          value: 'Label_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Actions',
          value: 'Actions',
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: 100,
          sortable: false
        }
      ]
    }
  },
  mounted () {
    this.getLabels()
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    canUpdate () {
      return !this.currentItem.Label_Name?.trim()
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    ...mapGetters(['getUser']),
    existingLabelNames () {
      return this.labels.map(l => l.Label_Name.toLowerCase().trim())
    }
  },
  methods: {
    getLabels () {
      this.axios.get('/api/project_employee_labels', {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        this.labels = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    createNewLabel () {
      if (this.existingLabelNames.includes(this.newLabelName.toLowerCase().trim())) {
        this.$store.dispatch('setError', { errorMsg: `Label "${this.newLabelName}" already exists` })
        return
      }

      this.axios.post('/api/project_employee_labels', {
        Label_Name: this.newLabelName
      }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.newLabelName = null
        this.getLabels()
        this.createNewLabelDialog = false
        this.$store.dispatch('setSuccess', { successMsg: 'Label successfully created' })
      }).catch(error => {
        if (error.response?.status === 500 && error.response.data.includes('duplicate key')) {
          this.$store.dispatch('setError', { errorMsg: `Label "${this.newLabelName}" already exists` })
        } else {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        }
      })
    },
    onEditClick (item, index) {
      if (this.editMode) {
        this.updateLabel()
      } else {
        this.tableHeaders[0].sortable = false
        this.currentItem = Object.assign({}, item)
        this.currentRowIndex = index
      }
      this.editMode = !this.editMode
    },
    onCancelClick () {
      this.editMode = false
      this.currentItem = null
      this.currentRowIndex = null
      this.tableHeaders[0].sortable = true
    },
    updateLabel () {
      const newName = this.currentItem.Label_Name.toLowerCase().trim()
      const duplicate = this.labels.some(
        l => l.Label_Name.toLowerCase().trim() === newName && l.Label_ID !== this.currentItem.Label_ID
      )

      if (duplicate) {
        this.$store.dispatch('setError', { errorMsg: `Label "${this.currentItem.Label_Name}" already exists` })
        return
      }

      this.axios.put(`/api/project_employee_labels/${this.currentItem.Label_ID}`, {
        Label_Name: this.currentItem.Label_Name
      }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.getLabels()
        this.onCancelClick()
        this.$store.dispatch('setSuccess', { successMsg: 'Label successfully updated' })
      }).catch(error => {
        if (error.response?.status === 500 && error.response.data.includes('duplicate key')) {
          this.$store.dispatch('setError', { errorMsg: `Label "${this.currentItem.Label_Name}" already exists` })
        } else {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        }
      })
    },
    onDeleteClick (item) {
      this.deleteLabelDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteLabelDialog = false
      this.currentItem = null
    },
    deleteLabel () {
      this.axios.delete(`/api/project_employee_labels/${this.currentItem.Label_ID}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(() => {
        this.deleteLabelDialog = false
        this.getLabels()
        this.$store.dispatch('setSuccess', { successMsg: 'Label successfully deleted' })
      }).catch(error => {
        const errorMsg = error.response?.data || error.message

        if (errorMsg.includes('conflicted with the REFERENCE constraint')) {
          this.$store.dispatch('setError', { errorMsg: 'Cannot delete label because it is used in another module.' })
        } else {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + errorMsg })
        }
      })
    }
  }
}
</script>
